/**
 * userCategoryMap.js
 *
 * Maps each POS username to the category name keywords they are allowed to see.
 * Keywords are matched case-insensitively against Main_CatName from the API.
 * The resolved Main_CatID values are then used to filter products by P_MID.
 *
 * Username  → allowed category name keywords (array of strings)
 *
 * Special value: null → the user sees ALL categories (no restriction)
 *
 * To add or change a user's access, edit the USER_CATEGORY_MAP below.
 */

const USER_CATEGORY_MAP = {
  // Liquor admin: only sees categories whose name includes "Liquor", "Spirits", "Beer", "Wine"
  Adminl: ["Liquor", "Spirits", "Beer", "Wine"],

  // Bakery admin: only sees categories whose name includes "Bakery"
  Adminb: ["Bakery"],

  // Pharmacy admin: only sees categories whose name includes "Pharmacy"
  Adminp: ["Pharmacy"],

  // Supermarket admin: only sees categories whose name includes "Supermarket"
  Admins: ["Supermarket"],
};

/**
 * Returns the list of allowed category name keywords for the given username.
 *
 * @param {string} username
 * @returns {string[] | null}
 *   - null  → no restriction, show all
 *   - []    → unknown user, show nothing (safe default)
 *   - [...] → show only matching categories
 */
export const getAllowedCategoryKeywords = (username) => {
  if (!username) return null;

  if (Object.prototype.hasOwnProperty.call(USER_CATEGORY_MAP, username)) {
    return USER_CATEGORY_MAP[username];
  }

  const lowerUser = username.toLowerCase();
  const matchedKey = Object.keys(USER_CATEGORY_MAP).find(
    (k) => k.toLowerCase() === lowerUser
  );
  if (matchedKey) return USER_CATEGORY_MAP[matchedKey];

  // Unknown username → show all (treat as unrestricted)
  return null;
};

/**
 * Given the full list of categories from the API and a username,
 * returns the Set of Main_CatID strings the user is allowed to see.
 * Returns null if the user has no restriction (sees everything).
 *
 * @param {Array}  allCategories  - raw API array: [{ Main_CatID, Main_CatName, ... }]
 * @param {string} username
 * @returns {Set<string> | null}
 */
export const getAllowedCategoryIds = (allCategories, username) => {
  const keywords = getAllowedCategoryKeywords(username);
  if (keywords === null) return null; // no restriction

  const allowedIds = new Set();
  allCategories.forEach((cat) => {
    const name = (cat.Main_CatName || '').toLowerCase();
    const match = keywords.some((kw) => name.includes(kw.toLowerCase()));
    if (match) allowedIds.add(String(cat.Main_CatID));
  });
  return allowedIds;
};

export default USER_CATEGORY_MAP;
